import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import TextField from '../../../../components/forms/TextField';
import FieldError from '../../../../components/forms/FieldError';
import Switch from '../../../../components/forms/Switch';
import {
    color_shades_darkest,
    color_shades_darker,
    color_shades_light,
    color_shades_lighter,
    color_text_link,
    color_colors_decline,
    color_defaults_white,
    color_gradients_green_shift,
} from '../../../../constants/colors';
import {
    buildSonioxContext,
    formatSonioxContextJson,
    getSonioxContextSerializedLength,
    getSonioxContextValidationError,
    getSonioxExtraFields,
    getSonioxGeneralHints,
    getSonioxTerms,
    normalizeSonioxContextJson,
    parseSonioxContext,
    SONIOX_CONTEXT_MAX_LENGTH,
    serializeSonioxContext,
    SonioxContextGeneralHint,
} from './sonioxContextUtils';

interface SonioxContextEditorProps {
    name: string;
    value?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    onTouch: () => void;
    fieldContainerStyle?: string;
    description?: string;
    title?: string;
    emptyGeneralHintsText?: string;
    showPreview?: boolean;
    showFieldError?: boolean;
    error?: string;
    maxLength?: number;
}

const emptyGeneralHint = (): SonioxContextGeneralHint => ({ key: '', value: '' });

const SonioxContextEditor: React.FC<SonioxContextEditorProps> = ({
    name,
    value,
    onChange,
    onBlur,
    onTouch,
    fieldContainerStyle,
    description,
    title,
    emptyGeneralHintsText,
    showPreview = true,
    showFieldError = true,
    error,
    maxLength,
}) => {
    const [advancedMode, setAdvancedMode] = useState(false);
    const [advancedValue, setAdvancedValue] = useState('');
    const editorDescription = description ?? 'Configure Soniox context per telephony connection. The structured builder covers common hints and terms, and advanced mode lets you edit the raw JSON object directly.';
    const [generalHints, setGeneralHints] = useState([] as SonioxContextGeneralHint[]);
    const [terms, setTerms] = useState([] as string[]);
    const [termInput, setTermInput] = useState('');
    const [extraFields, setExtraFields] = useState({} as Record<string, unknown>);
    const internalUpdateRef = useRef<string | null>(null);

    const activeMaxLength = maxLength ?? SONIOX_CONTEXT_MAX_LENGTH;

    const syncFromValue = useCallback((nextValue?: string) => {
        const validationError = getSonioxContextValidationError(nextValue, activeMaxLength);

        if (!!validationError.length) {
            setAdvancedValue(nextValue ?? '');
            setAdvancedMode(true);
            return;
        }

        const parsedContext = parseSonioxContext(nextValue);

        setGeneralHints(getSonioxGeneralHints(parsedContext));
        setTerms(getSonioxTerms(parsedContext));
        setExtraFields(getSonioxExtraFields(parsedContext));
        setAdvancedValue(formatSonioxContextJson(nextValue));
    }, [activeMaxLength]);

    useEffect(() => {
        if (internalUpdateRef.current) {
            internalUpdateRef.current = null;
            return;
        }

        syncFromValue(value);
    }, [syncFromValue, value]);

    const applyBuilderChange = (nextGeneralHints: SonioxContextGeneralHint[], nextTerms: string[], nextExtraFields = extraFields) => {
        setGeneralHints(nextGeneralHints);
        setTerms(nextTerms);
        setExtraFields(nextExtraFields);

        const nextContext = buildSonioxContext(nextExtraFields, nextGeneralHints, nextTerms);
        const serializedContext = serializeSonioxContext(nextContext);

        setAdvancedValue(formatSonioxContextJson(serializedContext));
        internalUpdateRef.current = 'builder';
        onChange(serializedContext);
        onTouch();
    };

    const handleGeneralHintChange = (index: number, field: 'key' | 'value', fieldValue: string) => {
        const nextGeneralHints = [...generalHints];
        const currentGeneralHint = nextGeneralHints[index] ?? emptyGeneralHint();

        nextGeneralHints[index] = {
            ...currentGeneralHint,
            [field]: fieldValue,
        };

        applyBuilderChange(nextGeneralHints, terms);
    };

    const handleAddGeneralHint = () => {
        setGeneralHints((prevGeneralHints) => [...prevGeneralHints, emptyGeneralHint()]);
    };

    const handleRemoveGeneralHint = (index: number) => {
        const nextGeneralHints = generalHints.filter((_, currentIndex) => currentIndex !== index);
        applyBuilderChange(nextGeneralHints, terms);
    };

    const handleAddTerms = (rawValue: string) => {
        const nextTerms = [...terms, ...rawValue.split(/[,\n]/g)];
        applyBuilderChange(generalHints, nextTerms);
        setTermInput('');
    };

    const handleTermInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            if (!!termInput.trim().length) {
                handleAddTerms(termInput);
            }
        }
    };

    const handleRemoveTerm = (termToRemove: string) => {
        applyBuilderChange(generalHints, terms.filter((term) => term !== termToRemove));
    };

    const handleAdvancedModeChange = (checked: boolean) => {
        if (checked) {
            setAdvancedValue(formatSonioxContextJson(value));
            setAdvancedMode(true);
            return;
        }

        const validationError = getSonioxContextValidationError(advancedValue, activeMaxLength);

        if (!!validationError.length) {
            onTouch();
            return;
        }

        const normalizedValue = normalizeSonioxContextJson(advancedValue);
        syncFromValue(normalizedValue);
        internalUpdateRef.current = 'builder';
        onChange(normalizedValue);
        onTouch();
        setAdvancedMode(false);
    };

    const currentValue = advancedMode ? advancedValue : value;
    const validationError = getSonioxContextValidationError(currentValue, activeMaxLength);
    const parsedCurrentContext = !!validationError.length ? null : parseSonioxContext(currentValue);
    const extraFieldKeys = useMemo(() => {
        const activeExtraFields = parsedCurrentContext ? getSonioxExtraFields(parsedCurrentContext) : extraFields;
        return Object.keys(activeExtraFields);
    }, [extraFields, parsedCurrentContext]);
    const payloadPreview = !!validationError.length ? '' : normalizeSonioxContextJson(currentValue);
    const serializedLength = !!validationError.length ? 0 : getSonioxContextSerializedLength(currentValue);

    return (
        <Container>
            <HeaderRow>
                <HeaderCopy>
                    <Label>{title ?? 'Soniox Context'}</Label>
                    {!!editorDescription.length && <Description>{editorDescription}</Description>}
                    {!!extraFieldKeys.length && (
                        <HelperText>
                            Preserving advanced fields: {extraFieldKeys.join(', ')}
                        </HelperText>
                    )}
                </HeaderCopy>
                <AdvancedToggle
                    checked={advancedMode}
                    onChange={handleAdvancedModeChange}
                    label="Advanced JSON"
                    detail={advancedMode ? 'Raw editor enabled' : 'Use the structured builder'}
                />
            </HeaderRow>

            {!advancedMode && (
                <BuilderGrid>
                    <EditorCard>
                        <CardHeader>
                            <CardTitle>General Hints</CardTitle>
                            <InlineButton type="button" onClick={handleAddGeneralHint}>
                                + Add Hint
                            </InlineButton>
                        </CardHeader>
                        <CardDescription>
                            Add repeatable key and value hints like domain, topic, or other call context.
                        </CardDescription>
                        {!generalHints.length && (
                            <EmptyState>
                                {emptyGeneralHintsText ?? 'No general hints yet. Add a row to include common Soniox context keys.'}
                            </EmptyState>
                        )}
                        {generalHints.map((generalHint, index) => (
                            <GeneralHintRow key={`general-hint-${index}`}>
                                <GeneralHintField
                                    useForm={false}
                                    label={index === 0 ? 'Key' : undefined}
                                    placeholder="domain"
                                    value={generalHint.key}
                                    onChange={(e) => handleGeneralHintChange(index, 'key', e.target.value)}
                                    onBlur={onBlur}
                                    fieldContainerStyle={fieldContainerStyle}
                                />
                                <GeneralHintField
                                    useForm={false}
                                    label={index === 0 ? 'Value' : undefined}
                                    placeholder="Support"
                                    value={generalHint.value}
                                    onChange={(e) => handleGeneralHintChange(index, 'value', e.target.value)}
                                    onBlur={onBlur}
                                    fieldContainerStyle={fieldContainerStyle}
                                />
                                <RemoveTextButton
                                    type="button"
                                    onClick={() => handleRemoveGeneralHint(index)}
                                >
                                    Remove
                                </RemoveTextButton>
                            </GeneralHintRow>
                        ))}
                    </EditorCard>

                    <EditorCard>
                        <CardHeader>
                            <CardTitle>Terms</CardTitle>
                        </CardHeader>
                        <CardDescription>
                            Add important words or phrases. Press Enter or comma to create a term. Blank values are ignored and duplicates are removed.
                        </CardDescription>
                        <TermInputRow>
                            <TermInputField
                                useForm={false}
                                label="Add Terms"
                                placeholder="Voicify, pepperoni, mozza-loaded"
                                value={termInput}
                                onChange={(e) => setTermInput(e.target.value)}
                                onKeyDown={handleTermInputKeyDown}
                                onBlur={() => {
                                    if (!!termInput.trim().length) {
                                        handleAddTerms(termInput);
                                    }
                                    onBlur();
                                }}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <AddTermButton
                                type="button"
                                disabled={!termInput.trim().length}
                                onClick={() => handleAddTerms(termInput)}
                            >
                                Add
                            </AddTermButton>
                        </TermInputRow>
                        {!!terms.length && (
                            <TermChipContainer>
                                {terms.map((term) => (
                                    <TermChip key={term}>
                                        <span>{term}</span>
                                        <TermChipButton type="button" onClick={() => handleRemoveTerm(term)}>
                                            Remove
                                        </TermChipButton>
                                    </TermChip>
                                ))}
                            </TermChipContainer>
                        )}
                    </EditorCard>
                </BuilderGrid>
            )}

            {advancedMode && (
                <EditorCard>
                    <CardHeader>
                        <CardTitle>Advanced JSON</CardTitle>
                    </CardHeader>
                    <CardDescription>
                        Enter a JSON object. The builder keys are preserved alongside any extra top-level fields you add here.
                    </CardDescription>
                    <RawJsonTextArea
                        name={name}
                        value={advancedValue}
                        placeholder={`{
  "general": [{ "key": "domain", "value": "Support" }],
  "terms": ["Voicify"]
}`}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setAdvancedValue(nextValue);
                            internalUpdateRef.current = 'advanced';
                            onChange(nextValue);
                            onTouch();
                        }}
                        onBlur={onBlur}
                    />
                </EditorCard>
            )}

            {showPreview && (
                <PreviewCard>
                    <CardHeader>
                        <CardTitle>Payload Preview</CardTitle>
                        <PreviewMeta>{serializedLength}/{activeMaxLength} chars</PreviewMeta>
                    </CardHeader>
                    <CardDescription>
                        This is the JSON string that will be sent as <code>sonioxContextJson</code>.
                    </CardDescription>
                    <PreviewBox>
                        {!!payloadPreview.length ? payloadPreview : 'Empty string will be sent.'}
                    </PreviewBox>
                </PreviewCard>
            )}

            {showFieldError && <FieldError name={name} />}
            {!showFieldError && !!error?.length && <InlineError>{error}</InlineError>}
        </Container>
    );
};

const Container = styled.div`
    margin-bottom: 24px;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
    flex: 1;
    min-width: 280px;
`;

const Label = styled.p`
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0;
`;

const Description = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin: 0;
`;

const HelperText = styled.p`
    font-size: 12px;
    color: ${color_shades_darker};
    margin: 8px 0 0 0;
`;

const InlineError = styled.div`
    border: 1px solid ${color_colors_decline};
    border-radius: 8px;
    color: ${color_shades_darkest};
    font-size: 14px;
    margin: 8px 0;
    padding: 8px 16px;
`;

const BuilderGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
`;

const EditorCard = styled.div`
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: ${color_defaults_white};
    padding: 16px;
    margin-bottom: 16px;
`;

const PreviewCard = styled(EditorCard)`
    margin-bottom: 0;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
`;

const CardTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0;
`;

const CardDescription = styled.p`
    font-size: 13px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin: 0 0 16px 0;
`;

const EmptyState = styled.div`
    font-size: 13px;
    color: ${color_shades_darker};
    border: 1px dashed ${color_shades_light};
    border-radius: 8px;
    padding: 16px;
`;

const GeneralHintRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 12px;
    align-items: end;
    margin-bottom: 8px;
`;

const GeneralHintField = styled(TextField)`
    margin-bottom: 0;
`;

const InlineButton = styled.button`
    border: none;
    background: none;
    color: ${color_text_link};
    font-size: 13px;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: ${color_shades_darker};
    }
`;

const RemoveTextButton = styled(InlineButton)`
    margin-bottom: 24px;

    &:hover {
        color: ${color_colors_decline};
    }
`;

const TermInputRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: end;
    margin-bottom: 8px;
`;

const TermInputField = styled(TextField)`
    flex: 1;
    margin-bottom: 0;
`;

const AddTermButton = styled.button`
    border: none;
    border-radius: 8px;
    background-image: ${color_gradients_green_shift};
    color: ${color_defaults_white};
    padding: 12px 16px;
    cursor: pointer;
    height: 48px;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const TermChipContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const TermChip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 999px;
    border: 1px solid ${color_shades_lighter};
    background: ${color_defaults_white};
    color: ${color_shades_darkest};
    font-size: 13px;
`;

const TermChipButton = styled.button`
    border: none;
    background: none;
    color: ${color_text_link};
    cursor: pointer;
    font-size: 12px;
    padding: 0;

    &:hover {
        color: ${color_colors_decline};
    }
`;

const RawJsonTextArea = styled.textarea`
    width: 100%;
    min-height: 240px;
    border-radius: 8px;
    border: 1px solid ${color_shades_light};
    padding: 16px;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${color_shades_darkest};
    resize: vertical;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${color_shades_darker};
    }
`;

const PreviewBox = styled.pre`
    margin: 0;
    border-radius: 8px;
    border: 1px solid ${color_shades_light};
    background: ${color_defaults_white};
    padding: 16px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${color_shades_darkest};
`;

const PreviewMeta = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
`;

const AdvancedToggle = styled(Switch)`
    flex-shrink: 0;
`;

export default SonioxContextEditor;
